import type { IssueActivity, IssueComment } from '@outpost/api-client';

/**
 * An issue's history, told in words, and woven into its comments.
 *
 * The history says who resolved, ignored or assigned an issue, and whether an
 * application did it on their behalf. Told alongside the comments rather than
 * in a panel of its own, because "Ada resolved this" is only half a story
 * without the comment above it saying why.
 */

/** One thing that happened on an issue, in the order it happened. */
export type TimelineItem =
  | { kind: 'comment'; at: string; comment: IssueComment }
  | { kind: 'activity'; at: string; activity: IssueActivity };

/**
 * Comments and history in one list, oldest first.
 *
 * Both arrive sorted already, so this is a merge rather than a sort; at an equal
 * timestamp the history entry comes first, since a change is more often
 * explained by the comment after it than before.
 */
export function timeline(comments: IssueComment[], activity: IssueActivity[]): TimelineItem[] {
  const items: TimelineItem[] = [];
  let c = 0;
  let a = 0;
  while (c < comments.length || a < activity.length) {
    const comment = comments[c];
    const entry = activity[a];
    if (entry && (!comment || entry.created_at <= comment.created_at)) {
      items.push({ kind: 'activity', at: entry.created_at, activity: entry });
      a += 1;
    } else if (comment) {
      items.push({ kind: 'comment', at: comment.created_at, comment });
      c += 1;
    }
  }
  return items;
}

/**
 * Who a history entry names as having acted.
 *
 * `null` for what Outpost did by itself — the sentence says so instead — and
 * "A former member" when the account behind a person's change has since gone.
 */
export function activityActor(entry: IssueActivity, youId?: string): string | null {
  if (isAutomatic(entry)) return null;
  if (youId && entry.actor_id === youId) return 'You';
  return entry.actor_name || entry.actor_email || 'A former member';
}

/** Whether Outpost made this change by itself, rather than a person. */
export function isAutomatic(entry: IssueActivity): boolean {
  return (
    entry.kind === 'regressed' ||
    entry.kind === 'ignore_expired' ||
    entry.kind === 'assignee_lost_access'
  );
}

/**
 * What happened, as the rest of a sentence starting with {@link activityActor}
 * — or, for Outpost's own changes, as a sentence of its own.
 */
export function describeActivity(entry: IssueActivity, youId?: string): string {
  switch (entry.kind) {
    case 'resolved':
      return 'resolved this issue';
    case 'unresolved':
      return 'reopened this issue';
    case 'ignored':
      return entry.ignored_until
        ? `ignored this issue until ${new Date(entry.ignored_until).toLocaleString()}`
        : 'ignored this issue';
    case 'assigned': {
      if (youId && entry.assignee_id === youId) {
        return entry.actor_id === youId ? 'took this issue' : 'assigned this issue to you';
      }
      const assignee = entry.assignee_name || entry.assignee_email || 'a former member';
      return `assigned this issue to ${assignee}`;
    }
    case 'unassigned':
      return 'unassigned this issue';
    case 'regressed':
      return 'Reopened as a regression: a new event arrived after it was resolved';
    case 'ignore_expired':
      return 'Reopened: the ignore ran out and a new event arrived';
    case 'assignee_lost_access':
      return 'Unassigned: the assignee lost access to this project';
  }
}
